import React, { useEffect, useState } from 'react'
import {
  Box,
  Grid,
  InputAdornment,
  TablePagination,
  TextField,
  Typography,
} from '@material-ui/core'
import { Search as SearchIcon } from 'react-feather'
import { isEmpty } from 'lodash'

import { Card, LoadingFeedback } from 'components'

import useFetch from 'hooks/useFetch'
import usePhishing from 'hooks/usePhishing'
import useSnackbar from 'hooks/useSnackbar'
import * as service from 'service'

import TemplateCard from './components/TemplateCard'
import PreviewModal from './components/PreviewModal'
import useStyles from './styles'

const TemplateStep = () => {
  const classes = useStyles()
  const [loading, setLoading] = useState(false)
  const [selectedTemplateId, setSelectedTemplateId] = useState(null)
  const [previewTemplate, setPreviewTemplate] = useState(null)
  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(8)

  const ROWS_PER_PAGE_OPTIONS = [8, 16, 24]

  const snackbar = useSnackbar()
  const {
    phishing,
    setPhishing,
    handleNext,
    isReadOnly,
    wantsToAdvanceStep,
    resetAdvanceStep,
  } = usePhishing()

  const { response: templatesResponse, isLoading: isTemplatesLoading } =
    useFetch(
      service.dponet.phishingTemplates.get,
      { page, perPage, name: debouncedSearch || undefined },
      [page, perPage, debouncedSearch],
    )

  const templates = templatesResponse?.data?.phishingTemplates || []
  const totalCount = templatesResponse?.data?.meta?.totalCount || 0

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput)
      setPage(1)
    }, 500)

    return () => clearTimeout(timer)
  }, [searchInput])

  const handleChangePage = (event, newPage) => {
    setPage(newPage + 1)
  }

  const handleChangeRowsPerPage = (event) => {
    setPerPage(parseInt(event.target.value, 10))
    setPage(1)
  }

  useEffect(() => {
    if (phishing?.phishingTemplateId) {
      setSelectedTemplateId(phishing.phishingTemplateId)
    }
  }, [phishing?.phishingTemplateId])

  const onSubmit = async () => {
    if (!selectedTemplateId) {
      snackbar.open({
        message: 'Selecione um modelo de e-mail para continuar.',
        variant: 'warning',
      })
      resetAdvanceStep()
      return
    }

    setLoading(true)
    try {
      await service.dponet.phishingCampaigns.configStep(phishing.id, {
        phishingCampaign: { phishingTemplateId: selectedTemplateId },
      })
      await service.dponet.phishingCampaigns.templatesStep(phishing.id)

      setPhishing((previous) => ({
        ...previous,
        phishingTemplateId: selectedTemplateId,
        phishingTemplate:
          templates.find((t) => t.id === selectedTemplateId) || null,
      }))

      resetAdvanceStep()
      handleNext()
    } catch {
      snackbar.open({
        message: 'Erro ao salvar modelo da campanha.',
        variant: 'error',
      })
      resetAdvanceStep()
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (wantsToAdvanceStep) {
      onSubmit()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantsToAdvanceStep])

  const handleSelectTemplate = (templateId) => {
    if (isReadOnly) return
    setSelectedTemplateId(templateId)
  }

  const handleSendEmailTest = async (template) => {
    setLoading(true)
    try {
      await service.dponet.phishingTemplates.sendEmailTest(template.id)
      snackbar.open({
        message: 'E-mail de teste enviado com sucesso.',
        variant: 'success',
      })
    } catch {
      snackbar.open({
        message: 'Erro ao enviar e-mail de teste.',
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <LoadingFeedback open={loading || isTemplatesLoading} />

      <PreviewModal
        open={Boolean(previewTemplate)}
        onClose={() => setPreviewTemplate(null)}
        onBack={() => setPreviewTemplate(null)}
        template={previewTemplate}
        onSendTest={handleSendEmailTest}
      />

      <Card>
        <Box p={2}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h3">
              Escolha um modelo de e-mail para sua campanha
            </Typography>
          </Box>

          <TextField
            fullWidth
            placeholder="Buscar modelo por nome"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            variant="outlined"
            disabled={isReadOnly}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon size={20} />
                </InputAdornment>
              ),
            }}
          />

          <Box mt={2}>
            {!isTemplatesLoading && isEmpty(templates) ? (
              <Typography className={classes.emptyState}>
                {isEmpty(debouncedSearch)
                  ? 'Nenhum modelo disponível.'
                  : 'Nenhum modelo encontrado para a busca.'}
              </Typography>
            ) : (
              <>
                <Grid container spacing={2}>
                  {templates.map((template) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={template.id}>
                      <TemplateCard
                        template={template}
                        isSelected={selectedTemplateId === template.id}
                        isReadOnly={isReadOnly}
                        onSelect={handleSelectTemplate}
                        onPreview={setPreviewTemplate}
                      />
                    </Grid>
                  ))}
                </Grid>

                <TablePagination
                  component="div"
                  count={totalCount}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  page={page - 1}
                  rowsPerPage={perPage}
                  rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                  labelRowsPerPage="Por página"
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} de ${count}`
                  }
                  nextIconButtonProps={{ size: 'small' }}
                  backIconButtonProps={{ size: 'small' }}
                />
              </>
            )}
          </Box>
        </Box>
      </Card>
    </>
  )
}

export default TemplateStep
